/**
 * <cart-notification>: slides in when an item is added to the cart.
 * Announcement happens via a dedicated always-rendered live region;
 * auto-dismiss is long (20s) and pauses while hovered or focused.
 */
import { subscribe, PUB_SUB_EVENTS, formatMoney } from './global.js';

const AUTO_DISMISS_MS = 20000;

class CartNotification extends HTMLElement {
  connectedCallback() {
    this.announcer = document.getElementById('CartLiveRegion');
    this.closeButton = this.querySelector('[data-notification-close]');
    this.closeButton?.addEventListener('click', () => this.close());

    this.onKeyup = (e) => {
      if (e.key === 'Escape' && !this.hidden) this.close();
    };
    document.addEventListener('keyup', this.onKeyup);

    this.addEventListener('mouseenter', () => this.pauseDismiss());
    this.addEventListener('focusin', () => this.pauseDismiss());
    this.addEventListener('mouseleave', () => this.resumeDismiss());
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.resumeDismiss();
    });

    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, ({ source, item, cart }) => {
      if (source !== 'product-form' || !item) return;
      this.render(item, cart);
      this.open();
      if (this.announcer) {
        const template =
          window.shorelineStrings?.cartAdded || '[product] added to cart. [count] items in cart.';
        this.announcer.textContent = template
          .replace('[product]', item.product_title)
          .replace('[count]', cart.item_count);
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener('keyup', this.onKeyup);
    this.unsubscribe?.();
    clearTimeout(this.autoClose);
    clearTimeout(this.hideTimer);
  }

  render(item, cart) {
    this.querySelector('[data-notification-count]').textContent = cart.item_count;
    const sep = item.image && item.image.includes('?') ? '&' : '?';
    const ratio = item.featured_image?.aspect_ratio || 1;
    this.querySelector('[data-notification-product]').innerHTML = `
      ${item.image ? `<img src="${encodeURI(item.image)}${sep}width=140" alt="" width="70" height="${Math.round(70 / ratio)}" loading="lazy">` : ''}
      <div>
        <p class="main-cart__product-title">${this.escape(item.product_title)}</p>
        ${item.variant_title ? `<p class="text-body-sm">${this.escape(item.variant_title)}</p>` : ''}
        ${item.selling_plan_allocation ? `<p class="text-body-sm">${this.escape(item.selling_plan_allocation.selling_plan.name)}</p>` : ''}
      </div>`;
    this.renderShippingBar(cart);
  }

  renderShippingBar(cart) {
    const bar = this.querySelector('[data-notification-shipping]');
    if (!bar) return;
    const threshold = parseInt(bar.dataset.thresholdCents, 10);
    if (!threshold || typeof cart.total_price !== 'number') return;
    const remaining = threshold - cart.total_price;
    const message = bar.querySelector('[data-notification-shipping-message]');
    if (remaining > 0) {
      const template =
        window.shorelineStrings?.freeShippingRemaining || "You're [remaining] away from free shipping";
      message.textContent = template.replace('[remaining]', formatMoney(remaining));
    } else {
      message.textContent = window.shorelineStrings?.freeShippingUnlocked || "You've unlocked free shipping";
    }
    const fill = bar.querySelector('[data-notification-shipping-fill]');
    fill.style.width = `${Math.min(100, (cart.total_price / threshold) * 100)}%`;
    bar.hidden = false;
  }

  escape(text) {
    const div = document.createElement('div');
    div.textContent = text ?? '';
    return div.innerHTML;
  }

  open() {
    clearTimeout(this.hideTimer);
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-open'));
    this.resumeDismiss();
  }

  pauseDismiss() {
    clearTimeout(this.autoClose);
  }

  resumeDismiss() {
    clearTimeout(this.autoClose);
    if (!this.hidden) this.autoClose = setTimeout(() => this.close(), AUTO_DISMISS_MS);
  }

  close() {
    this.classList.remove('is-open');
    clearTimeout(this.autoClose);
    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      this.hidden = true;
    }, 300);
  }
}
if (!customElements.get('cart-notification')) customElements.define('cart-notification', CartNotification);
